import { DeviceProfile } from '@/components/provider/UserCurrentProvider';

/**
 * Synchronously checks if the device is currently in a critically low battery state.
 * Returns a Promise resolving to a boolean.
 */
export async function checkLowBatteryStatus(): Promise<boolean> {
    if ('getBattery' in navigator) {
        try {
            const battery = await (navigator as any).getBattery();
            // Low if under 15% and drawing on battery juice (not charging)
            return battery.level < 0.15 && !battery.charging;
        } catch {
            return false;
        }
    }
    return false;
}

export function getDevicePerformanceProfile(): Omit<
    DeviceProfile,
    'lowBattery'
> {
    // Context Fix: Ensure touch-first environments (including DevTools emulation) override loose hover properties
    const isTouchFirst =
        typeof window !== 'undefined' &&
        (window.matchMedia('(pointer: coarse)').matches ||
            (navigator.maxTouchPoints && navigator.maxTouchPoints > 0));

    const profile: Omit<DeviceProfile, 'lowBattery'> = {
        deviceSpeed: 2, // Default: Standard Baseline
        deviceMemory: 2, // Default: Standard Baseline
        networkSpeed: 2, // Default: Average 4G
        devicePixelRatio:
            typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
        hasTrueHover:
            typeof window !== 'undefined'
                ? isTouchFirst
                    ? false
                    : window.matchMedia('(hover: hover)').matches
                : true,
    };

    // --- 1. Network Conditions ---
    if ('connection' in navigator) {
        const conn = (navigator as any).connection;
        if (
            conn.saveData ||
            conn.effectiveType === '2g' ||
            conn.effectiveType === '3g'
        ) {
            profile.networkSpeed = 3; // Slowest
        } else if (conn.downlink >= 15 || conn.effectiveType === '4g') {
            profile.networkSpeed = conn.downlink >= 30 ? 1 : 2; // 1 is Best
        }
    }

    // --- 2. Hardware Memory (RAM) ---
    if ('deviceMemory' in navigator) {
        const ram = (navigator as any).deviceMemory;
        if (ram >= 8)
            profile.deviceMemory = 1; // Best
        else if (ram <= 2)
            profile.deviceMemory = 3; // Unsupported/Slow
        else profile.deviceMemory = 2; // Baseline (4GB-6GB)
    }

    // --- 3. iOS Memory Fallback (e.g., iPhone 6s Plus Baseline) ---
    const isIOS =
        typeof window !== 'undefined' &&
        /iPad|iPhone|iPod/.test(navigator.userAgent) &&
        !(window as any).MSStream;
    const screenWidth = typeof window !== 'undefined' ? window.screen.width : 0;

    if (isIOS && !('deviceMemory' in navigator)) {
        if (screenWidth < 414) {
            profile.deviceMemory = 3;
        } else {
            profile.deviceMemory = 2; // Baseline matched
        }
    }

    // --- 4. Device Speed / CPU Processing (e.g., 2017 Dual/Quad Core Baselines) ---
    if ('hardwareConcurrency' in navigator) {
        const cores = navigator.hardwareConcurrency;
        if (cores >= 8)
            profile.deviceSpeed = 1; // Best
        else if (cores <= 2)
            profile.deviceSpeed = 3; // Slowest (Dual-core or throttled)
        else profile.deviceSpeed = 2; // Baseline (4-6 logical cores)
    }

    if (profile.deviceMemory === 3) {
        profile.deviceSpeed = 3;
    }

    return profile;
}
